import { useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Upload, Download, Trash2 } from "lucide-react";

/**
 * Per-car document slot. `value` is { path, filename } | null.
 * onChange(newVal) is called after upload or remove.
 */
export default function CarDocSlot({ label, value, onChange, accept = "application/pdf", testid }) {
  const ref = useRef();

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ path: data.path, filename: data.filename });
      toast.success(`${label}: încărcat`);
    } catch {
      toast.error("Upload eșuat");
    } finally {
      e.target.value = "";
    }
  }

  async function download() {
    if (!value?.path) return;
    try {
      const r = await api.get("/files/serve", { params: { path: value.path }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = value.filename || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("Descărcare eșuată");
    }
  }

  return (
    <div className="border border-slate-200 rounded-xl p-3 flex items-center gap-3" data-testid={testid}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${value ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>
        <FileText className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-slate-500 truncate">
          {value?.filename || "Niciun fișier încărcat"}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {value && (
          <>
            <button onClick={download} title="Descarcă" data-testid={`${testid}-download`}
              className="p-2 rounded-lg hover:bg-slate-100 text-slate-600">
              <Download className="w-4 h-4" />
            </button>
            <button onClick={() => onChange(null)} title="Șterge" data-testid={`${testid}-remove`}
              className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600">
              <Trash2 className="w-4 h-4" />
            </button>
          </>
        )}
        <button onClick={() => ref.current?.click()} data-testid={`${testid}-upload`}
          className="px-3 py-2 rounded-lg bg-[#0A0F1C] text-white text-xs inline-flex items-center gap-1 hover:bg-[#1A1F2C]">
          <Upload className="w-3.5 h-3.5" /> {value ? "Înlocuiește" : "Încarcă PDF"}
        </button>
        <input ref={ref} type="file" accept={accept} className="hidden" onChange={handleFile} />
      </div>
    </div>
  );
}
